import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityTypeEnum,
  BookingTaxEstimateInterface,
} from '@sport-activities/sdk';

import { BookingService } from '~/checkout/services/booking/booking.service';
import { CheckboxAlignEnum } from '~/component/form/checkbox/enums/checkbox-align.enum';
import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { LoaderService } from '~/core/services/loader/loader.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { UserService } from '~/auth/services/user/user.service';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { map, mergeMap } from 'rxjs/operators';
import { DisplayPricePipe } from '~/core/pipes/display-price.pipe';
import { CurrencyPipe, isPlatformBrowser } from '@angular/common';
import { toDecimalPrice, toIntegerPrice } from '~/core/helpers/numbers.helper';
import { CartService } from '~/checkout/services/cart/cart.service';
import { TaxEstimateService } from '~/checkout/services/tax-estimate/tax-estimate.service';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayIconButtonVariantEnum } from '~/component/buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-variant.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { BmsTermsInterface } from '~/checkout/interfaces/bms.interface';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
})
export class CartComponent implements OnInit, OnDestroy {
  @Output() public login = new EventEmitter();
  private readonly subscriptions = new Subscription();

  private isLoggedIn: boolean;

  public readonly checkboxAlign = CheckboxAlignEnum.Top;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayIconButtonVariant = VtmnPlayIconButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public isCartEmpty = true;

  public bmsTerms: BmsTermsInterface[];
  public legalTermsText = '';
  public isBmsConsentMandatory: boolean;

  public cartItems: CheckoutItem[];
  public cartSubmissionDisabled = false;
  public consent = false;
  public imageRightsConsent = false;
  public hasClicked = false;
  public isCartOpen: boolean;
  public lowerSize = false;
  public taxes: BookingTaxEstimateInterface;
  public overlayStatus = SuccessStatusEnum.LOADING;
  public isRedirect: boolean;
  public isLoading: boolean;

  constructor(
    private readonly bookingService: BookingService,
    private readonly cartService: CartService,
    private readonly checkoutService: CheckoutService,
    private readonly currencyPipe: CurrencyPipe,
    private readonly loaderService: LoaderService,
    private readonly redirectionService: RedirectionService,
    private readonly paramethorService: ParamethorService,
    @Inject(TranslateService)
    @Inject(PLATFORM_ID)
    private readonly platformId: string,
    private readonly translateService: TranslateService,
    private readonly userService: UserService,
    private readonly taxEstimateService: TaxEstimateService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit() {
    this.subscriptions.add(
      this.loaderService.loading$.subscribe(() => {
        this.isLoading = this.loaderService.isLoading(
          'CART_SUBSCRIPTION_LOADER_KEY'
        );
      })
    );

    this.checkoutService.getCartItems().subscribe({
      next: (items) => {
        this.cartItems = items
          ? items.map((item) => new CheckoutItem(item))
          : [];
        this.isCartEmpty = this.cartItems?.length === 0;

        if (!this.isCartEmpty && this.isBms) {
          this.bmsTerms = [].concat(
            ...(this.cartItems ?? []).map((objet) => objet.bmsTerms).flat()
          );

          this.isBmsConsentMandatory = this.bmsTerms?.some(
            (item) => item?.requiresExplicitConsent === true
          );

          if (!this.isBmsConsentMandatory && this.isBms) {
            this.consent = true;
          }

          this.legalTermsTranslation();
        }
      },
      error: (error) => {},
    });

    this.subscriptions.add(
      this.userService.isAuthenticated().subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
        if (
          this.isBrowser &&
          this.isLoggedIn &&
          localStorage.getItem('isOpen') === 'isOpen'
        ) {
          this.isRedirect = true;
          localStorage.setItem('isOpen', 'isClose');
          this.submitCart();
        }
      })
    );

    this.subscriptions.add(
      this.cartService.isCartOpen.subscribe((isOpen) => {
        this.isCartOpen = isOpen;
      })
    );

    if (!this.showExternalPayment && this.cartItems?.length > 0) {
      this.subscriptions.add(
        this.taxEstimateService.getTaxes(this.cartItems).subscribe((taxes) => {
          this.taxes = taxes;
        })
      );
    }

    this.legalTermsTranslation();
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  // Taxes always returned, check if they are the same amount, in this case we have no taxes
  public get isTaxesDisplayed(): boolean {
    return (
      this.taxes.totalWithTax[0]?.amount !==
      this.taxes.totalWithoutTax[0]?.amount
    );
  }

  public get cartIcon(): VtmnPlayIconTypeEnum {
    return VtmnPlayIconTypeEnum.ShoppingBag;
  }

  public get totalTimeslots(): number {
    return this.cartItems?.length;
  }

  public get cartSubtotal(): string {
    if (this.totalTimeslots === 1) {
      return this.translateService.instant('checkout.cart.subtotal_single');
    }
    return this.translateService.instant('checkout.cart.subtotal', {
      totalTimeslots: this.totalTimeslots?.toString(),
    });
  }

  public get cartTitle(): string {
    return this.translateService.instant('checkout.cart.label');
  }

  public get isBms(): boolean {
    return this.cartItems?.[0]?.activity.isBms;
  }

  public get bmsRequireConsent(): boolean {
    return this.cartItems?.[0]?.activity.isBms;
  }

  public legalTermsTranslation(): string {
    if (this.isBms && this.bmsTerms?.length > 0) {
      this.legalTermsText = this.checkoutService.getBmsTermsTrans(
        this.bmsTerms,
        this.isBmsConsentMandatory
      );
      return this.legalTermsText;
    }

    let translationKey: string;
    let params: Record<string, string> = {};

    if (!this.isBms) {
      translationKey = 'checkout.summary.conditions.free';
      params = { termsOfUse: String(this.legalTermsUsageServiceUrl) };
    }

    this.legalTermsText = this.translateService.instant(translationKey, params);
    return this.legalTermsText;
  }

  public get legalTermsUsageServiceUrl(): object | string {
    return this.paramethorService.get(
      ParamethorNameEnum.LegalTermsUsageParticipantUrl
    );
  }

  public get linkIcon(): VtmnPlayIconTypeEnum {
    return VtmnPlayIconTypeEnum.Link;
  }

  public get totalItemsInCart(): string {
    if (this.isBms) {
      return this.translateService.instant('checkout.cart.total.items', {
        cartTotalItems: '1',
        cartLimit: '1',
      });
    }
    return this.translateService.instant('checkout.cart.total.items', {
      cartTotalItems: this.getCartItemsNumber(),
      cartLimit: 3,
    });
  }

  public get showExternalPayment(): boolean {
    return this.paramethorService.isTrue(
      ParamethorNameEnum.AllowExternalPayment
    );
  }

  public get totalPrice(): string {
    if (!this.cartItems && !this.cartItems?.length) {
      return '';
    }

    const displayPricePipe = new DisplayPricePipe(
      this.currencyPipe,
      this.translateService
    );

    const currency = this.cartItems[0]?.currency;
    let totalPrice = 0;

    this.cartItems?.forEach((checkoutItem: CheckoutItem) => {
      totalPrice += toIntegerPrice(checkoutItem.totalPrice);
    });

    return displayPricePipe.transform(
      toDecimalPrice(totalPrice),
      currency
    );
  }

  public get cartIsFull(): boolean {
    const maxItemsInCart = this.isBms ? 1 : 3;
    return Number(this.getCartItemsNumber()) >= maxItemsInCart;
  }

  public get noCartSubmission(): boolean {
    return this.cartSubmissionDisabled === true;
  }

  public get cartButtonTranslation(): string {
    return this.checkoutService.isFreeCheckout()
      ? this.translateService.instant('checkout.cart.free.continue')
      : this.translateService.instant('checkout.cart.continue');
  }

  public get isFreeCheckout(): boolean {
    return this.checkoutService.isFreeCheckout();
  }

  public disableCartSubmission(value): void {
    this.cartSubmissionDisabled = value;
  }

  public checkUserConnexion(): void {
    this.hasClicked = true;

    if (!this.isLoggedIn) {
      localStorage.setItem('isOpen', 'isOpen');
      if (!this.isBms) {
        sessionStorage.setItem(
          'imageRightConsent',
          this.imageRightsConsent.toString()
        );
      }
      this.login.emit();
      return;
    }

    this.submitCart();
  }

  public getCartItemsNumber(): string {
    this.lowerSize = this.cartItems?.length > 9;
    return this.cartItems?.length.toString();
  }

  public openCartContent(): void {
    this.cartService.openCart();
  }

  public closeCartContent(): void {
    this.cartService.closeCart();
  }

  public goToActivity(): void {
    this.closeCartContent();
    this.redirectionService.redirect(
      `/attendee.show-activity/${this.cartItems[0]?.activity?.sku}`
    );
  }

  public isActivityEvent(): boolean {
    return this.cartItems[0]?.activity.type === ActivityTypeEnum.EVENT;
  }

  private handlePostConfirmation(bookingId?: string): Promise<boolean> {
    this.deleteAllCartContent();
    this.cartService.closeCart();
    if (sessionStorage.getItem('imageRightConsent')) {
      sessionStorage.removeItem('imageRightConsent');
    }
    return bookingId
      ? this.checkoutService.redirectToConfirmation(bookingId)
      : this.checkoutService.redirectToConfirmation();
  }

  private submitCart(): void {
    // Is paying booking
    if (!this.showExternalPayment && !this.checkoutService.isFreeCheckout()) {
      this.cartService.closeCart();
      this.redirectionService.redirect('/checkout.billing-address');
      return;
    }
    if (this.isBms) {
      const bookingId = localStorage.getItem('bookingId');
      this.subscriptions.add(
        this.bookingService.confirm(bookingId).subscribe(() => {
          localStorage.removeItem('bookingId');
          this.handlePostConfirmation();
        })
      );
      return;
    }

    const itemsInCart = this.cartItems
      .map((item, i) => ({
        ...item,
        ...(!this.isBms && {
          isImageRightsConsented: sessionStorage.getItem('imageRightConsent')
            ? JSON.parse(sessionStorage.getItem('imageRightConsent'))
            : this.imageRightsConsent,
        }),
      }))
      .map((checkoutItem) => new CheckoutItem(checkoutItem));
    this.subscriptions.add(
      this.bookingService
        .start(itemsInCart)
        .pipe(
          mergeMap((bookingResponse) => {
            const bookingId = bookingResponse.bookingId;

            if (!this.isActivityEvent) {
              return this.bookingService
                .confirm(bookingId)
                .pipe(map(() => null));
            }

            return this.bookingService.getBookingItemId(bookingId).pipe(
              mergeMap((data) => {
                const identifier = data.items?.[0]?.identifier;
                this.bookingService.setBookingId(identifier);
                return this.bookingService
                  .confirm(bookingId)
                  .pipe(map(() => identifier));
              })
            );
          })
        )
        .subscribe((activityIdentifier?: string | null) => {
          this.handlePostConfirmation(activityIdentifier ?? undefined);
        })
    );
  }

  public deleteAllCartContent(): void {
    this.checkoutService.emptyCart();
  }
}
